/**
 * Chart DOM measurement + theme colors. These touch the
 * `<plot-chart>`-owned nodes (handed to `runtime` via `attachDom`) but write no
 * atoms - pure geometry + CSS-var reads, shared by the renderers and the resize
 * seams in `app-root`.
 */

import { runtime } from '../plot-runtime.ts';

export function getCssVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

export interface ThemeColors {
  text: string;
  textStrong: string;
  grid: string;
  surface: string;
  accent: string;
  accentGlow: string;
}

export function themeColors(): ThemeColors {
  return {
    text: getCssVar('--text-dim') || '#8a9099',
    textStrong: getCssVar('--text') || '#e0e0e0',
    grid: getCssVar('--grid') || '#2a2d36',
    surface: getCssVar('--surface') || '#1a1d23',
    accent: getCssVar('--accent') || '#6384ff',
    accentGlow: getCssVar('--accent-glow') || 'rgba(99,132,255,0.18)',
  };
}

export interface ChartSize {
  width: number;
  height: number;
}

/**
 * Inner size for the uPlot canvas, reserving room for its legend so canvas +
 * legend together fit the overflow-clipped card (blob `chartContainerSize`).
 *
 * Measures `#chart-root`'s content box directly (`clientWidth`/`clientHeight`)
 * so the size handed to uPlot matches the box it actually renders into. uPlot
 * then owns its element dimensions - no CSS `!important` overrides - which keeps
 * the canvas-drawn lines aligned with the DOM-positioned cursor points/tooltip.
 */
export function chartContainerSize(): ChartSize {
  const el = runtime.rootEl;
  if (!el || el.offsetParent === null) return { width: 600, height: 360 };
  const legendEl = el.querySelector<HTMLElement>('.u-legend');
  let legendH = 0;
  if (legendEl && legendEl.offsetParent !== null) {
    const lr = legendEl.getBoundingClientRect();
    const ls = getComputedStyle(legendEl);
    legendH =
      Math.ceil(lr.height) +
      Math.ceil(parseFloat(ls.marginTop) || 0) +
      Math.ceil(parseFloat(ls.marginBottom) || 0);
  }
  return {
    width: Math.max(80, el.clientWidth),
    height: Math.max(80, el.clientHeight - legendH),
  };
}

export function ensurePlotResizeObs(): void {
  if (runtime.resizeObs || !runtime.containerEl) return;
  runtime.resizeObs = new ResizeObserver(() => {
    if (!runtime.instance) return;
    runtime.instance.setSize(chartContainerSize());
  });
  runtime.resizeObs.observe(runtime.containerEl);
  // Also watch #chart-root, the box `chartContainerSize` actually measures.
  //
  // On the first plot of a session the root is still `display:none` (the empty
  // placeholder is up) when the renderer measures it, so `offsetParent` is null
  // and the fallback 600x360 is used - the "tiny chart until you resize the
  // window" bug. The container does not change size when the root becomes
  // visible, so watching only the container never corrected it; watching the
  // root does, on the very next frame.
  if (runtime.rootEl) runtime.resizeObs.observe(runtime.rootEl);
}

export function teardownPlotResizeObs(): void {
  runtime.resizeObs?.disconnect();
  runtime.resizeObs = null;
  runtime.gridResizeObs?.disconnect();
  runtime.gridResizeObs = null;
}

/** Resize the live chart to its container (tab/divider/right-tab seams). */
export function resizePlot(): void {
  if (runtime.instance) runtime.instance.setSize(chartContainerSize());
}
